<template>

	<div class="sortable-list-container" :class="{'full-width': fullWidth}">

		<div ref="list" class="sortable-list">
			<div v-for="(item, index) in currItems" class="list-item" :class="{'no-sort': item.noSort}" :data-index="item.hasOwnProperty(keyProp) ? item[keyProp] : index" :key="item.hasOwnProperty(keyProp) ? item[keyProp] : index">
				<slot :name="item.customSlot" :item="item" :index="index">
					<div class="list-item-header">
						<img v-if="currItems.length && currItems.length > 1" class="handle icon-dimension-20" :src="'https://img.qromo.io/img/vue/icons/icon-sort-list-item'+(userStore.darkMode ? '-dark' : '')+'.svg'" :title="getString('drag_to_sort')" :alt="getString('drag_to_sort')" draggable="false">
						<p class="list-item-title">{{ item.title }}</p>
						<AppButtonIcon
							v-if="item.dropdownSlot"
							icon-name="icon-edit-white"
							small
							:active="!!item.active"
							@click="createDropdownOnExpand ? toggleDropdownDelayed(index) : item.active = !item.active" />
					</div>
				</slot>
				<slot :name="item.headerSlot" :item="item" :index="index"></slot>
				<AppDropdown v-if="item.dropdownSlot" :collapsed="createDropdownOnExpand ? item.collapsedDelayed : !item.active">
					<slot v-if="!createDropdownOnExpand || item.active" :name="item.dropdownSlot" :item="item" :index="index" />
				</AppDropdown>
			</div>
			<div class="list-item list-item-add no-sort" @click="$emit('add-item')" v-if="hasAddItem">
				<img :src="'https://img.qromo.io/img/vue/icons/icon-plus'+(userStore.darkMode ? '-dark' : '')+'.svg'" draggable="false" class="icon-dimension-15 mr-10" alt="icon plus">
				<p class="list-item-title">{{ getString(addText) }}</p>
			</div>
		</div>

	</div>

</template>

<script>

import AppButtonIcon from "@/shared/components/AppButtonIcon.vue";
import AppDropdown from "@/shared/components/AppDropdown.vue";
import {useLanguageStore, useScriptStore} from "@/shared/stores";
import {useUserStore} from "@/console-new/stores";

export default {
	setup() {
		const languageStore = useLanguageStore();
		const scriptStore = useScriptStore();
		let userStore = useUserStore();
		return {getString: languageStore.getString, loadScript: scriptStore.loadScript, userStore};
	},
	emits: ["sorting", "sorted", "add-item"],
	name: "AppSortableList",
	components: {AppButtonIcon, AppDropdown},
	data() {
		return {
			currItems: this.items,
			sortable: null,
		}
	},
	props: {
		items: {type: Array, required: true, validator(value) {
			return value.every(item => {
				return item.customSlot || item.title;
			});
		}},
		hasAddItem: {type: Boolean, default: true},
		createDropdownOnExpand: {type: Boolean, default: false},
		fullWidth: {type: Boolean, default: false},
		keyProp: {type: String, default: null},
		addText: {type: String, default: 'add_item'},
	},
	methods: {
		initSortable() {
			let self = this;
			this.sortable = new Sortable(this.$refs.list, {
				handle: '.handle',
				filter: '.no-sort',
				animation: 250,
				dataIdAttr: 'data-index',
				forceFallback: true,
				onStart: evt => {
					self.$emit("sorting", evt, self.getSortedItems());
				},
				onEnd: evt => {
					self.$emit("sorted", evt, self.getSortedItems());
				},
				onMove: evt => evt.related.className.indexOf('no-sort') === -1,
			});
		},
		getSortedItems() {
			let newOrder = this.sortable.toArray();
			let newItems = [];
			newOrder.forEach(index => {
				let item = this.currItems.find(item => item.hasOwnProperty(this.keyProp) ? item[this.keyProp] === index : item === index);
				if(item) {
					newItems.push(item);
				}
			});
			return newItems;
		},
		toggleDropdownDelayed(itemIndex) {
			let self = this;
			for(let i = 0; i < this.currItems.length; i++) {
				const iT = i;
				if(i === itemIndex) {
					if(!this.currItems[i].active) {
						this.currItems[i].active = true;
						requestAnimationFrame(() => {
							requestAnimationFrame(() => {
								if(this.currItems[i] && self.currItems[iT]) {
									self.currItems[iT].collapsedDelayed = !this.currItems[i].active;
								}
							});
						});
					} else if(!this.currItems[i].collapsedDelayed) {
						this.currItems[i].collapsedDelayed = true;
						setTimeout(() => {
							if(this.currItems[i] && self.currItems[iT]) {
								self.currItems[iT].active = !this.currItems[i].collapsedDelayed;
							}
						}, 520);
					}
				} else {
					if(!this.currItems[i].collapsedDelayed) {
						this.currItems[i].collapsedDelayed = true;
						setTimeout(() => {
							if(this.currItems[i] && self.currItems[iT]) {
								self.currItems[iT].active = !this.currItems[i].collapsedDelayed;
							}
						}, 520);
					}
				}
			}

		},
	},
	mounted() {
		this.loadScript("sortable",() => {
			this.initSortable();
		});
	},
	beforeUnmount() {
		if(this.sortable) {
			this.sortable.destroy();
		}
	},
	watch: {
		items(newItems) {
			this.currItems = newItems;
		}
	}
}
</script>

<style lang="scss" scoped>
.icon-dimension-20 {
	width: 20px;
	height: 20px;
}
.sortable-list-container {
	width: 100%;
	border-radius: 13px;
	box-shadow: 0 1px 2px var(--color-box-shadow-3);
	margin: 0 auto;
	max-width: 680px;

	.icon-dimension-15 {
		width: 15px;
		height: 15px;
	}

	&.full-width {
		max-width: 1000px;
	}

	.list-item {
		@include row;
		@include justifyFlex(between);
		@include alignFlex(center);
		width: 100%;
		min-height: 45px;
		padding: 10px;
		position: relative;
		background-color: var(--color-bg-primary-1);

		.list-item-header {
			display: flex;
			align-items: center;
			justify-content: space-between;
			width: 100%;
		}

		&:nth-of-type(1) {
			border-top-left-radius: 13px;
			border-top-right-radius: 13px;
		}

		&:last-of-type {
			border-bottom-left-radius: 13px;
			border-bottom-right-radius: 13px;
		}

		&.list-item-add {
			background-color: var(--color-bg-primary-2);
			cursor: pointer;

			& * {
				@include noSelect;
				pointer-events: none;
			}

			.list-item-title {
				width: calc(100% - 30px);
				font-weight: 500;
				color: $primary;
			}
		}

		&:before {
			@include pseudo;
			top: 0;
			left: 0;
			height: 1px;
			width: 100%;
			background-color: var(--color-border-alpha);
		}

		&:before {
			top: 0;
		}

		&:nth-of-type(1):before {
			display: none;
		}

		.list-item-title {
			@include wordWrap;
			font-size: 16px;
			width: calc(100% - 180px);
			min-width: 120px;
			margin-right: auto;
		}

		.handle, :slotted(.handle) {
			cursor: move;
			width: 20px;
			margin-right: 10px;
		}

		@media (max-width: 768px) {
			.list-item-title {
				min-width: 100px;
				width: calc(100% - 160px);
			}
			&.list-item-add {
				.list-item-title {
					width: unset;
				}
			}
		}
		@media (max-width: 560px) {
			.list-item-title {
				width: calc(100% - 140px);
				min-width: 80px;
				font-size: 14px;
			}
			&.list-item-add {
				.list-item-title {
					width: unset;
				}
			}
		}

		@media (max-width: 400px) {
			&.list-item-add {
				.list-item-title {
					width: unset;
					font-size: 12px;
				}
			}
		}

	}

}

</style>